import React, { useMemo } from 'react';

import {
  bookingInquiryLogApis,
  dashboardBookingInquiryLogApis,
} from '@apis/next/bookingInquiryLogApis';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { NewPageContainer } from '@components/NewPageContainer';
import { BookingInquiryLogItem } from '@components/utilities/BookingInquiry/BookingInquiryLogItem';
import { mapBookingInquiryChangeLogsToBookingInquiryChangeLogGroups } from '@data/bookingInquiryChangeLog';
import { useSearch } from '@hooks/next/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { toJST } from '@utils/types';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: '予約内容変更履歴',
    back: '予約詳細ページへ',
    noLogs: '変更履歴はありません',
  },
});

const useStyles = makeMuiStyles((theme) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
}));

export const BookingInquiryLogs: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    search: { d2d_no, admin = false },
  } = useSearch(
    z.object({
      d2d_no: z.string(),
      admin: z.boolean().optional(),
    }),
  );

  const bookingInquiryLogs = admin
    ? dashboardBookingInquiryLogApis.useGet('/dashboard/booking-inquiry/:d2d_no/logs', {
        params: {
          d2d_no,
        },
      })
    : bookingInquiryLogApis.useGet('/booking-inquiry/:d2d_no/logs', {
        params: {
          d2d_no,
        },
      });

  const bookingInquiryChangeLogGroups = useMemo(() => {
    return bookingInquiryLogs.data
      ? mapBookingInquiryChangeLogsToBookingInquiryChangeLogGroups(bookingInquiryLogs.data)
      : [];
  }, [bookingInquiryLogs.data]);

  const latestUpdate =
    bookingInquiryChangeLogGroups[0]?.createdAt &&
    toJST(new Date(bookingInquiryChangeLogGroups[0]?.createdAt));

  return (
    <Layout
      title={strings.title}
      hideHeader
      hideFooter
      hideCookiePolicy
      classes={{
        background: classes.background,
      }}
      loading={bookingInquiryLogs.isLoading}
      errors={[bookingInquiryLogs.error]}
    >
      <NewPageContainer
        title={`${strings.title}-${d2d_no}`}
        subtitle={
          (bookingInquiryLogs.data?.length ?? -1) > 0
            ? `Latest Update: ${latestUpdate}`
            : strings.noLogs
        }
        action={
          !admin ? (
            <a href={`${url('/utilities/Bookings/BookingInquiryHistoryDetails')}?d2dNo=${d2d_no}`}>
              <Button color="primary" text={strings.back} size="small" />
            </a>
          ) : null
        }
      >
        {bookingInquiryChangeLogGroups.map((group) => (
          <BookingInquiryLogItem key={group.groupId} item={group} admin={admin} d2dNo={d2d_no} />
        ))}
      </NewPageContainer>
    </Layout>
  );
};
